// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbar } from "@bg/XDL/Utilities";
import NavigationDrawer from "@bgs/Overrides/Components/NavigationDrawer";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesContext } from "@insite/content-library/additionalStyles";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import { MenuPresentationProps } from "@insite/mobius/Menu";
import Page from "@insite/mobius/Page";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    openParentPages = "openParentPages",
    links = "links",
    l2Links = "l2Links",
    showQuickOrder = "showQuickOrder",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.openParentPages]: boolean;
        [fields.links]: LinkModel[];
        [fields.l2Links]: LinkModel[];
        [fields.showQuickOrder]: boolean;
        [fields.customCSS]: string;
    };
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
}

interface LinkModel {
    fields: {
        linkType: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overrideTitle: string;
        viewAllLinkImage?: string;
        title: string;
        l2Links?: any[];
    };
}

export interface MappedLink {
    title: string;
    url: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    linkType?: string;
    mappedLinks2?: any[];
    l2Links?: any[];
    imageUrl?: string;
    type?: string;
    viewAllLinkImage?: string;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const {
        fields: { links },
    } = ownProps;
    const mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};
    // const isVmiAdmin = session.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    // const isVmiUser = session.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;

    if (links && links?.length > 0) {
        for (const link of links) {
            const {
                fields: { linkType, destination, openInNewWindow, overrideTitle, l2Links, viewAllLinkImage },
            } = link;
            const { type, value } = destination;

            const depthToLoad = 10;

            let mappedLink: MappedLink | undefined;

            if (type === "Url") {
                mappedLink = {
                    url: value,
                    title: overrideTitle,
                };
            } else if (type === "Page") {
                const readonlyPageLink = getPageLinkByNodeId(state, value);
                if (readonlyPageLink) {
                    mappedLink = { ...readonlyPageLink };
                    if (overrideTitle) {
                        mappedLink.title = overrideTitle;
                    }
                }
            } else if (type === "Category") {
                mappedLink = setupCategoryLink(state, value, categoryIdsToLoad, depthToLoad, overrideTitle);
            }

            if (!mappedLink) {
                continue;
            }

            mappedLink.linkType = linkType;

            if (linkType !== "Link" && l2Links && l2Links.length > 0) {
                mappedLink.l2Links = l2Links;
            }

            mappedLink.openInNewWindow = openInNewWindow;
            mappedLink.viewAllLinkImage = viewAllLinkImage;

            mappedLinks.push(mappedLink);
        }
    }

    if (mappedLinks && mappedLinks?.length > 0) {
        for (const l1link of mappedLinks) {
            if (
                l1link.linkType !== "Menu" ||
                !l1link.l2Links ||
                (l1link.linkType === "Menu" && l1link.l2Links?.length < 1)
            ) {
                continue;
            }
            const mappedLinks2 = [];

            for (const link of l1link.l2Links) {
                const destination = link.fields.destination;
                const overrideTitleL2 = link.fields.overrideTitleL2;
                const imageLink = link.fields.imageLink;
                const { type, value } = destination;

                const depthToLoad = 10;

                let mappedLink: MappedLink | undefined;

                if (type === "Url") {
                    mappedLink = {
                        url: value,
                        title: overrideTitleL2 ?? value,
                    };
                } else if (type === "Page") {
                    const readonlyPageLink = getPageLinkByNodeId(state, value);
                    if (readonlyPageLink) {
                        mappedLink = { ...readonlyPageLink };
                    }
                    if (overrideTitleL2 && mappedLink) {
                        mappedLink.title = overrideTitleL2;
                    }
                } else if (type === "Category") {
                    mappedLink = setupCategoryLink(state, value, categoryIdsToLoad, depthToLoad, overrideTitleL2);
                }

                if (!mappedLink) {
                    continue;
                }

                mappedLink.imageUrl = imageLink || "";

                mappedLinks2.push(mappedLink);
            }
            l1link.mappedLinks2 = mappedLinks2;
        }
    }

    const pageType = getCurrentPage(state).type;

    return {
        links: mappedLinks,
        quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage"),
        categoryIdsToLoad,
        allowQuickOrder: getSettingsCollection(state).orderSettings.allowQuickOrder,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        isSticky: state.xHeader.isSticky,
        featuredBrands: state.xHeader.featuredBrands,
        pageType,
        categoriesPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
        displayChangeCustomerLink: state.context?.session?.displayChangeCustomerLink,
        categoriesDataView: getCategoriesDataView(state),
        // Xng-Change: comment out as not used
        // displayVmiNavigation:
        //     currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
        // displayModeSwitch: isAuthenticated && orderSettings.vmiEnabled && (isVmiAdmin || isVmiUser),
        // currentMode,
        // vmiPageLinks: isVmiAdmin
        //     ? [
        //           getPageLinkByPageType(state, "VmiDashboardPage"),
        //           getPageLinkByPageType(state, "VmiLocationsPage"),
        //           getPageLinkByPageType(state, "VmiUsersPage"),
        //           getPageLinkByPageType(state, "VmiBinsPage"),
        //           getPageLinkByPageType(state, "VmiReportingPage"),
        //           getPageLinkByPageType(state, "VmiOrderHistoryPage"),
        //       ]
        //     : isVmiUser
        //     ? [
        //           getPageLinkByPageType(state, "VmiDashboardPage"),
        //           getPageLinkByPageType(state, "VmiLocationsPage"),
        //           getPageLinkByPageType(state, "VmiBinsPage"),
        //           getPageLinkByPageType(state, "VmiReportingPage"),
        //           getPageLinkByPageType(state, "VmiOrderHistoryPage"),
        //       ]
        //     : [],
    };
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const categoryState = getCategoryState(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);

    if (value === emptyGuid) {
        mappedLink = {
            url: "",
            title: overrideTitle || translate("Products"),
        };
    } else {
        if (!categoryState.value) {
            return;
        }
        mappedLink = {
            url: categoryState.value.path,
            title: overrideTitle || categoryState.value.shortDescription,
        };
    }

    loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

const mapDispatchToProps = {
    loadCategories,
    setNavDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

interface State {}

export interface MainNavigationStyles {
    menuHeight?: number;
    container?: InjectableCss;
    innerContainer?: InjectableCss;
    itemWrapper?: InjectableCss;
    quickOrderItemWrapper?: InjectableCss;
    menuItem?: InjectableCss;
    menuItemIcon?: IconProps;
    menuItemTypography?: TypographyPresentationProps;
    cascadingMenu?: MenuPresentationProps;
    megaMenu?: PopoverPresentationProps;
    megaMenuGridContainer?: GridContainerProps;
    megaMenuGridItem?: GridItemProps;
    megaMenuHeading?: LinkPresentationProps;
    megaMenuLink?: LinkPresentationProps;
    mobileWrapper?: InjectableCss;
    mobileMenuWrapper?: InjectableCss;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    l2LinkLazyImage?: LazyImageProps;
}

export const mainNavigationStyles: MainNavigationStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 55px;
            margin: 0 -10px;
        `,
    },
    innerContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
        `,
    },
    itemWrapper: {
        css: css`
            position: relative;
            overflow: visible;
            display: flex;
            &:hover span + div {
                display: block;
            }
        `,
    },
    quickOrderItemWrapper: {
        css: css``,
    },
    menuItem: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 10px;
            margin-right: 35px;
            text-decoration: none !important;
            &,
            p,
            span {
                color: ${getColor("text.main")} !important;
                font-size: ${fontConfig.fontSize.small}px;
                font-weight: ${fontConfig.weight.semiBold};
                letter-spacing: 1.17px;
                line-height: 17px;
                white-space: nowrap;
                text-transform: uppercase;

                @media screen and (max-width: 1250px) and (min-width: 1025px) {
                    font-size: ${fontConfig.fontSize.xSmall};
                }
            }

            &.shop-by-link {
                &[aria-expanded="true"] {
                    &,
                    p,
                    span {
                        color: ${getColor("tertiary")} !important;
                    }
                    svg {
                        transform: rotate(180deg);
                        transition: 0.2s;
                        color: ${getColor("tertiary")} !important;
                        fill: ${getColor("tertiary")} !important;
                    }
                }
            }
        `,
    },
    menuItemIcon: {
        css: css``,
        size: 9,
        color: "text.main",
    },
    menuItemTypography: {
        variant: "headerSecondary",
        color: "secondary.contrast",
    },
    mobileWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    mobileMenuWrapper: {
        css: css``,
    },
    popover: {
        transitionDuration: "short",
        toggle: false,
        css: css`
            background-color: ${getColor("common.ui4")};
            &:after {
                content: "";
                width: 100vw;
                height: 100%;
                position: fixed;
                background-color: rgba(0, 0, 0, 0.4);
                pointer-events: none;
                left: 0;
            }
        `,
    } as any,
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-start;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            min-height: 350px;
            height: 555px;
            max-height: calc(100vh - 200px);
            padding-top: 40px;
            padding-bottom: 10px;
            background-color: ${getColor("common.ui4")};
            ${styledScrollbar(5)}
        `,
    },
    l2LinkLazyImage: {
        css: css`
            &&& {
                width: auto;
            }
        `,
    },
};

const styles = mainNavigationStyles;

class MainNavigation extends React.Component<Props, State> {
    container = React.createRef<HTMLDivElement>();
    mobileSearchInput = React.createRef<HTMLInputElement>();
    declare context: React.ContextType<typeof StylesContext>;

    constructor(props: Props) {
        super(props);

        this.state = {};
    }

    popover = React.createRef<HTMLUListElement>();
    list = React.createRef<HTMLUListElement>();
    popover2 = React.createRef<HTMLUListElement>();
    list2 = React.createRef<HTMLUListElement>();

    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {}

    componentDidMount() {}

    loadCategoriesIfNeeded() {
        const { categoryIdsToLoad } = this.props;
        if (!this.props.categoriesDataView.isLoading && !this.props.categoriesDataView.value) {
            this.props.loadCategories();
        }
    }

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        const headerElement = document.getElementsByTagName("header")[0].getBoundingClientRect();

        if (element.current) {
            const rect = element.current.getBoundingClientRect();

            if (this.props.isSticky) {
                positionStyle.top = headerElement.height;
            } else {
                positionStyle.top = headerElement.height + headerElement.top;
            }
            positionStyle.right = 0;
            positionStyle.width = "100%";
        }

        return positionStyle;
    };

    closePopover = () => {
        if (!document) {
            return;
        }
        const triggers = document.getElementsByClassName("shop-by-link");
        for (const trigger of triggers) {
            if (trigger.getAttribute("aria-expanded")?.toString().toLowerCase() === "true") {
                (trigger as any).click();
            }
        }
    };

    render() {
        const {
            links,
            quickOrderLink,
            fields: { showQuickOrder, openParentPages, customCSS },
            id,
            allowQuickOrder,
            showFlyoutPricingToggle,
            showFlyoutInventoryToggle,
            shellContext,
        } = this.props;

        const showQuickOrderLink = showQuickOrder && allowQuickOrder;

        const downIcon = <StyledIcon src="KeyboardArrowDown" {...styles.menuItemIcon} />;

        const customCssWrapper = {
            css: css`
                ${customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper
                    {...styles.mobileMenuWrapper}
                    className="mobile-navigation-drawer-wrapper mobile-wrapper mobile-menu-wrapper"
                >
                    <NavigationDrawer
                        links={links}
                        showQuickOrder={showQuickOrderLink}
                        quickOrderLink={quickOrderLink}
                        rootElementSelector=".mobile-menu-wrapper"
                        allowOpenParentLinks={openParentPages}
                    />
                </StyledWrapper>
                <Hidden below="lg">
                    <StyledWrapper {...styles.container} ref={this.container}>
                        <StyledWrapper className="container" {...styles.innerContainer}>
                            {links.map((link, index) => {
                                if (
                                    link.linkType === "Link" &&
                                    (link?.excludeFromNavigation === false || link?.excludeFromNavigation === undefined)
                                ) {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper
                                            className="item-wrapper"
                                            {...styles.itemWrapper}
                                            key={`${link.title}_${index}`}
                                        >
                                            <Link
                                                typographyProps={styles.menuItemTypography}
                                                color={styles.menuItemTypography?.color}
                                                {...styles.menuItem}
                                                href={link.url}
                                                target={link.openInNewWindow ? "_blank" : ""}
                                            >
                                                {link.title}
                                            </Link>
                                        </StyledWrapper>
                                    );
                                }
                                if (
                                    link.linkType === "Menu" &&
                                    (link?.excludeFromNavigation === false || link?.excludeFromNavigation === undefined)
                                ) {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper
                                            className="item-wrapper"
                                            {...styles.itemWrapper}
                                            key={`${link.title}_${index}`}
                                        >
                                            <Popover
                                                {...styles.popover}
                                                controlsId={`${index}_${link.title}`}
                                                ref={this.popover}
                                                insideRefs={[this.list]}
                                                popoverTrigger={
                                                    <Link
                                                        aria-label={translate("Shop")}
                                                        typographyProps={styles.menuItemTypography}
                                                        color={styles.menuItemTypography?.color}
                                                        {...styles.menuItem}
                                                        className="shop-by-link"
                                                    >
                                                        {link.title} {downIcon}
                                                    </Link>
                                                }
                                                positionFunction={this.getPopoverPosition}
                                                wrapperProps={styles.popoverWrapper}
                                                contentBodyProps={
                                                    {
                                                        ref: this.list,
                                                        ...styles.popoverContentBody,
                                                    } as any
                                                }
                                                toggle={true}
                                            >
                                                <Page
                                                    css={css`
                                                        padding-top: 40px;
                                                        padding-bottom: 40px;
                                                    `}
                                                >
                                                    <GridContainer gap={40}>
                                                        {link?.mappedLinks2 &&
                                                            link?.mappedLinks2.map(
                                                                (l2Link, index) =>
                                                                    l2Link &&
                                                                    (l2Link?.excludeFromNavigation === false ||
                                                                        l2Link?.excludeFromNavigation === undefined) &&
                                                                    index < 9 && (
                                                                        <GridItem
                                                                            width={[12, 6, 4, 3, 2.4] as any}
                                                                            // eslint-disable-next-line react/no-array-index-key
                                                                            key={`${l2Link.title}_${index}`}
                                                                            css={css`
                                                                                min-height: 190px;
                                                                                * {
                                                                                    max-width: 100%;
                                                                                    width: 100%;
                                                                                }
                                                                            `}
                                                                        >
                                                                            <Link
                                                                                href={l2Link.url}
                                                                                onClick={this.closePopover}
                                                                                css={css`
                                                                                    height: 100%;
                                                                                    display: block;

                                                                                    > span {
                                                                                        height: 100%;
                                                                                        display: flex;
                                                                                        flex-direction: column;
                                                                                        justify-content: flex-start;
                                                                                        align-items: flex-start;
                                                                                    }
                                                                                `}
                                                                            >
                                                                                <LazyImage
                                                                                    altText={l2Link.title || ""}
                                                                                    src={l2Link.imageUrl}
                                                                                    width="100%"
                                                                                    height="190px"
                                                                                    cloudinaryWidth="240"
                                                                                    disableLazyLoad={true}
                                                                                    cloudinary={true}
                                                                                    {...styles.l2LinkLazyImage}
                                                                                />
                                                                                <Typography
                                                                                    size={fontConfig.fontSize.small}
                                                                                    weight={fontConfig.weight.semiBold}
                                                                                    lineHeight="17px"
                                                                                    color="text.main"
                                                                                    transform="uppercase"
                                                                                    css={css`
                                                                                        letter-spacing: 1px;
                                                                                        margin-top: 15px;
                                                                                    `}
                                                                                >
                                                                                    {l2Link.title}
                                                                                </Typography>
                                                                            </Link>
                                                                        </GridItem>
                                                                    ),
                                                            )}
                                                        <GridItem
                                                            width={[12, 6, 4, 3, 2.4] as any}
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            key={`${link.title}_${index}`}
                                                            css={css`
                                                                min-height: 190px;
                                                                * {
                                                                    max-width: 100%;
                                                                    width: 100%;
                                                                }
                                                            `}
                                                        >
                                                            <Link
                                                                href={link.url}
                                                                onClick={this.closePopover}
                                                                css={css`
                                                                    height: 100%;
                                                                    display: block;

                                                                    > span {
                                                                        height: 100%;
                                                                        display: flex;
                                                                        flex-direction: column;
                                                                        justify-content: space-between;
                                                                    }
                                                                `}
                                                            >
                                                                <LazyImage
                                                                    altText={translate(
                                                                        "View All {0} image",
                                                                        link.title,
                                                                    )}
                                                                    src={link.viewAllLinkImage}
                                                                    width="100%"
                                                                    height="190px"
                                                                    cloudinaryWidth="240"
                                                                    disableLazyLoad={true}
                                                                    cloudinary={true}
                                                                />
                                                                <Typography
                                                                    size={14}
                                                                    lineHeight="17px"
                                                                    ellipsis={true}
                                                                    color="text.main"
                                                                    css={css`
                                                                        text-transform: uppercase;
                                                                        letter-spacing: 1px;
                                                                        margin-top: 15px;
                                                                        font-weight: ${fontConfig.weight.semiBold};
                                                                    `}
                                                                >
                                                                    {translate("View All {0}", link.title)}
                                                                </Typography>
                                                            </Link>
                                                        </GridItem>
                                                    </GridContainer>
                                                </Page>
                                            </Popover>
                                        </StyledWrapper>
                                    );
                                }
                                return <></>;
                            })}
                            {showQuickOrderLink && quickOrderLink && (
                                <StyledWrapper className="item-wrapper" {...styles.itemWrapper}>
                                    <StyledWrapper
                                        className="quick-order-item-wrapper"
                                        {...styles.quickOrderItemWrapper}
                                    >
                                        <Link
                                            typographyProps={styles.menuItemTypography}
                                            color={styles.menuItemTypography?.color}
                                            {...styles.menuItem}
                                            id="quickOrder"
                                            href={quickOrderLink.url}
                                            className="quick-order-link"
                                        >
                                            {quickOrderLink.title}
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <StyledWrapper>
                            {createWidgetElement("XngBookingAppointmentDrawer/XngBookingAppointmentDrawer", {
                                fields: {},
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </Hidden>
            </StyledWrapper>
        );
    }
}

const defaultCustomCss = `.mobile-wrapper {
}

.mobile-menu-wrapper {
}

.menu-trigger-button {
}

.menu-trigger-button-icon {
}

.drawer {
}

.drawer-section-wrapper {
}

.currency-selector-wrapper {
}

.language-selector-wrapper {
}

.panel-menu {
}

.main-navigation-row {
}

.panel-section-wrapper {
}

.main-navigation-row-icon {
}

.user-row-text {
}

.punch-out-navigation-row-icon {
}

.punch-out-user-row-text {
}

.main-navigation-row-text {
}

.logo-links-row {
}

.logo-link-title {
}

.language-image {
}

.menu-row-icon {
}

.change-customer-row {
}

.change-customer-row-container {
}

.fulfillment-method-grid-item {
}

.addresses-grid-item {
}

.pick-up-address-grid-item {
}

.apply-button-grid-item {
}

.sign-out-row {
}

.sign-out-row-text {
}

.mobile-search-wrapper {
}

.mobile-search-button {
}

.mobile-search-button-icon {
}

.container {
}

.item-wrapper {
}

.cascading-menu {
}

.menu-item {
}

.mega-menu {
}

.mega-menu-grid-container {
}

.mega-menu-grid-item {
}

.mega-menu-heading-link {
}

.mega-menu-link {
}

.quick-order-item-wrapper {
}

.quick-order-link {
}

.vmi-page-link {
}

.mobile-search-modal {
}`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const mainNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(MainNavigation)),
    definition: {
        group: "Header",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.showQuickOrder,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                tab: basicTab,
                onEditRow: (index: number, dispatch) => {
                    dispatch({
                        type: "SendToSite/OpenMainNavigation",
                        index,
                    });
                },
                onDoneEditingRow: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                onLoad: dispatch => {
                    dispatch({
                        type: "SendToSite/CloseMainNavigation",
                    });
                },
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Menu",
                                value: "Menu",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        showCategoryRoot: item => true,
                    },
                    {
                        name: "overrideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "viewAllLinkImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: ({ fields }) => fields.linkType === "Menu",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: fields.l2Links,
                        editorTemplate: "ListField",
                        isVisible: item => item.fields.linkType === "Menu",
                        getDisplay: (item: HasFields, state) => {
                            const { type, value } = item.fields.destination;
                            const { overrideTitleL2, title } = item.fields;
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return overrideTitleL2 || link?.title;
                            }
                            if (type === "Category") {
                                if (overrideTitleL2) {
                                    return overrideTitleL2;
                                }
                                if (value === emptyGuid) {
                                    return "Products";
                                }
                                const categoryState = getCategoryState(state, value);
                                if (!categoryState.value) {
                                    if (!categoryState.isLoading && value) {
                                        return dispatch => {
                                            dispatch(
                                                loadCategory({
                                                    id: value,
                                                }),
                                            );
                                        };
                                    }
                                    return "";
                                }
                                return categoryState.value.shortDescription;
                            }
                            if (type === "Url") {
                                return overrideTitleL2 ?? value;
                            }
                            return value;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "destination",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Page",
                                },
                                isRequired: true,
                                showCategoryRoot: item => true,
                            },
                            {
                                name: "overrideTitleL2",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "imageLink",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                                isRequired: true,
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.openParentPages,
                displayName: "Open Parent Pages",
                tooltip:
                    "When enabled, tapping on a text link in the flyout menu will directly open the linked webpage and require users to click the chevron to access child pages. If disabled, tapping any link in the flyout menu will navigate to a submenu of child pages.",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default mainNavigation;
